import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  MdSearch, MdWhatsapp, MdCall, MdPerson, MdPayments, MdPrint,
  MdGroup, MdRefresh, MdPersonSearch, MdCreateNewFolder, MdFolderShared, MdVpnKey, MdRestore,
} from 'react-icons/md';
import useToastWithSound from '../../hooks/useToastWithSound';
import api, { session } from '../../ui/api';
import { useAuth } from '../auth';
import useData from '../useData';
import { useNavIntent, useNavigate } from '../navIntent';
import NewDossier from './dossier/NewDossier';
import DossierList, { dossierRanges } from './dossier/DossierList';
import DossierPrint from './dossier/DossierPrint';
import StudentLoginsModal, { StudentCodesStrip } from './StudentLoginsModal';
import ReactivateStudentModal from './ReactivateStudentModal';
import { localToday } from './dossier/dossierModel';
import { normalize } from '../search/searchEngine';
import { useCachedData, TTL, formatAge } from '../../ui/cache';
import PageShell, { Button } from '../../ui/PageShell';
import Card, { StatCard, StatGrid, EmptyState } from '../../ui/Card';
import Modal from '../../ui/Modal';
import Tabs from '../../ui/Tabs';
import { FormGrid, Field, Input, Select, Textarea } from '../../ui/Form';
import { StudentRecordDoc, MarksSheetDoc, PaymentReceiptDoc } from '../../ui/documents';
import './studentDirectory.css';
import { formatRegNo } from '../../ui/labels';

const money = (v) => `$${(v || 0).toLocaleString('en-US')}`;
const today = () => new Date().toISOString().slice(0, 10);

const STATUS_LABEL = {
  present: 'حاضر',
  absent: 'غائب',
  late: 'متأخر',
  no_register: 'لم يُتفقّد',
};

/** Digits only, with a leading + kept — what wa.me and tel: both want. */
const cleanPhone = (p) => (p || '').replace(/[^\d+]/g, '');
const waLink = (p) => `https://wa.me/${cleanPhone(p).replace(/^\+/, '')}`;

const initials = (name) => {
  const w = (name || '').trim().split(/\s+/);
  return w.length >= 2 ? w[0][0] + w[1][0] : (name || 'ط')[0];
};

/** One parent's phone with its two one-tap actions. The whole reason
 *  the office opens this screen is to reach a parent fast, so the
 *  number is never just text. */
function Contact({ who, name, phone }) {
  if (!phone) return null;
  return (
    <div className="sd-contact">
      <div className="sd-contact-text">
        <span className="sd-contact-who">{who}{name ? ` — ${name}` : ''}</span>
        <span className="sd-contact-num">{phone}</span>
      </div>
      <div className="sd-contact-btns">
        <a
          className="sd-icon-btn sd-icon-wa"
          href={waLink(phone)}
          target="_blank"
          rel="noreferrer"
          title={`واتساب ${who}`}
        >
          <MdWhatsapp size={16} />
        </a>
        <a className="sd-icon-btn sd-icon-call" href={`tel:${cleanPhone(phone)}`} title={`اتصال بـ${who}`}>
          <MdCall size={16} />
        </a>
      </div>
    </div>
  );
}

function Info({ label, value }) {
  if (value === null || value === undefined || value === '') return null;
  return (
    <div className="sd-info-item">
      <span className="sd-info-label">{label}</span>
      <span className="sd-info-value">{value}</span>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* شؤون الطلاب = three views: find a pupil (as before), open a new      */
/* dossier (إضبارة جديدة), and the dossiers opened lately (الإضبارات). */
/* ------------------------------------------------------------------ */

const VIEWS = [
  { id: 'search', label: 'البحث عن طالب', icon: MdPersonSearch },
  { id: 'new', label: 'إضبارة جديدة', icon: MdCreateNewFolder },
  { id: 'dossiers', label: 'الإضبارات', icon: MdFolderShared },
];
const VIEW_KEY = 'mtz.studentDirectory.view';

function ViewTabs({ views, view, onView, badge }) {
  return (
    <div className="sd-views" role="tablist">
      {views.map((v) => (
        <button
          key={v.id}
          type="button"
          role="tab"
          aria-selected={view === v.id}
          className={`sd-view sd-view-${v.id}${view === v.id ? ' is-active' : ''}`}
          data-guide={`dir-view-${v.id}`}
          onClick={() => onView(v.id)}
        >
          <v.icon size={19} />
          <span>{v.label}</span>
          {v.id === 'dossiers' && badge > 0 && <span className="sd-view-badge" title="إضبارات اليوم">{badge}</span>}
        </button>
      ))}
    </div>
  );
}

export default function StudentDirectory() {
  const { isOffice, isAdmin } = useAuth();
  const navigate = useNavigate();
  // Opening a new dossier (adding a pupil) is the manager's.
  const views = useMemo(() => (isAdmin ? VIEWS : VIEWS.filter((v) => v.id !== 'new')), [isAdmin]);
  const [view, setViewState] = useState(() => {
    if (!isOffice) return 'search';
    try { return views.some((v) => v.id === sessionStorage.getItem(VIEW_KEY)) ? sessionStorage.getItem(VIEW_KEY) : 'search'; } catch { return 'search'; }
  });
  // The other two views mount the first time they are opened, then stay
  // mounted (hidden): switching tabs keeps a half-typed dossier, the
  // search filters and the open profile exactly as they were.
  const [opened, setOpened] = useState(() => new Set(['search', view]));

  const setView = useCallback((v) => {
    const next = isOffice && views.some((x) => x.id === v) ? v : 'search';
    setViewState(next);
    setOpened((o) => (o.has(next) ? o : new Set([...o, next])));
    try { sessionStorage.setItem(VIEW_KEY, next); } catch { /* ignore */ }
  }, [isOffice, views]);

  // A tab is a history entry, so the browser's Back returns to the
  // previous view. navigate() puts {view} in the entry; the one entry
  // with no intent (arriving on the page) is the search view.
  const go = (v) => { if (v !== view) navigate('student-directory', { view: v }); };
  useEffect(() => {
    const onPop = (e) => {
      const s = e.state;
      if (s?.mtz && !s.guard && s.page === 'student-directory' && !s.intent) setView('search');
    };
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, [setView]);

  // «الإضبارات»: this month + this week + yesterday in one request, so
  // every chip there is instant — and the tab shows today's count.
  const day = localToday();
  const ranges = useMemo(() => dossierRanges(), [day]); // eslint-disable-line react-hooks/exhaustive-deps
  const feedData = useCachedData(
    () => (isOffice
      ? api.studentDossiers({ from: ranges.from.toISOString(), to: ranges.to.toISOString() })
      : Promise.resolve([])),
    [day, isOffice],
    { key: 'dossiers-list', ttl: TTL.VOLATILE }
  );
  const feed = {
    rows: feedData.data || [],
    loading: feedData.loading,
    error: feedData.error,
    reload: feedData.reload,
    revalidating: feedData.revalidating,
    ranges,
  };
  const todayCount = useMemo(() => {
    const [a, b] = ranges.spans.today;
    return (feedData.data || []).filter((r) => {
      const t = Date.parse(r.created_at);
      return t >= a.getTime() && t < b.getTime();
    }).length;
  }, [feedData.data, ranges]);

  const tabs = isOffice ? <ViewTabs views={views} view={view} onView={go} badge={todayCount} /> : null;

  return (
    <>
      <div hidden={view !== 'search'}>
        <DirectorySearch tabs={tabs} onView={setView} />
      </div>
      {opened.has('new') && (
        <div hidden={view !== 'new'}>
          <NewDossier tabs={tabs} active={view === 'new'} />
        </div>
      )}
      {opened.has('dossiers') && (
        <div hidden={view !== 'dossiers'}>
          <DossierList tabs={tabs} feed={feed} />
        </div>
      )}
    </>
  );
}

function DirectorySearch({ tabs, onView }) {
  const toast = useToastWithSound();
  const { user, isOffice, isAdmin } = useAuth();
  const [date, setDate] = useState(today());
  // 'enrolled' (default) | 'left' | 'all'. Pupils who have left are not
  // fetched at all unless asked for: this screen is the daily "who is
  // here and who do I call" tool, and a leaver in that list is a wrong
  // phone call. But the office still needs to reach them — a pupil who
  // left keeps their full profile, enrolments, marks and documents —
  // so it is one click, not a different page.
  const [enrollFilter, setEnrollFilter] = useState('enrolled');

  // VOLATILE, not ROSTER: these rows carry today's attendance state and
  // each pupil's payment balance. Both are acted on directly — the
  // office phones a parent about an absence, or takes money against a
  // balance — so a stale value here causes a real mistake, not just a
  // stale-looking screen. 20s is enough to make tab-switching instant
  // without ever showing yesterday's answer. `date` and `enrollFilter`
  // are in deps, so they are part of the cache key automatically.
  const { data, loading, error, reload, revalidating, offline, age } = useCachedData(
    () => Promise.all([
      api.studentDirectory(
        enrollFilter === 'enrolled' ? { date } : { date, include_inactive: 1 }
      ),
      api.sections(),
    ]).then(([dir, sections]) => ({ dir, sections })),
    [date, enrollFilter],
    { key: 'student-directory', ttl: TTL.VOLATILE }
  );

  const [search, setSearch] = useState('');
  const [gradeFilter, setGradeFilter] = useState('');
  const [sectionFilter, setSectionFilter] = useState('');
  const [todayFilter, setTodayFilter] = useState('all');
  const [payFilter, setPayFilter] = useState('');
  const [selectedId, setSelectedId] = useState(null);
  const [tab, setTab] = useState('overview');
  const [limit, setLimit] = useState(60);

  const students = useMemo(() => data?.dir?.students || [], [data]);
  const sections = data?.sections || [];

  const grades = useMemo(() => {
    const seen = new Map();
    students.forEach((s) => { if (s.grade_title) seen.set(s.grade_title, true); });
    return [...seen.keys()];
  }, [students]);

  // Each pupil's searchable text, normalised once per load rather than
  // per keystroke — same Arabic folding as the navbar search, so «احمد»
  // finds «أحمد» here too.
  const hay = useMemo(() => new Map(students.map((s) => [s.id, normalize([
    s.name, s.national_id, s.registration_number, s.father_name, s.mother_name, s.bus_number,
  ].filter(Boolean).join(' '))])), [students]);

  // One pass, applied in the order that eliminates the most rows first.
  // Runs entirely in memory on an already-loaded array, which is what
  // makes typing feel instant instead of firing a request per keystroke.
  const filtered = useMemo(() => {
    const q = normalize(search);
    const qDigits = search.replace(/\D/g, '').replace(/^0+/, '');
    return students.filter((s) => {
      // 'left' narrows the fetched set (which already includes leavers)
      // down to just them; 'all' shows both together.
      if (enrollFilter === 'left' && s.status === 'active') return false;
      if (gradeFilter && s.grade_title !== gradeFilter) return false;
      if (sectionFilter && String(s.section_id) !== String(sectionFilter)) return false;
      if (todayFilter !== 'all' && s.today_status !== todayFilter) return false;
      if (payFilter && s.payment_status !== payFilter) return false;
      if (!q) return true;
      if (q.split(' ').every((w) => hay.get(s.id)?.includes(w))) return true;
      // A local number (0536…) still finds the stored +90536….
      return qDigits.length >= 3 && (
        cleanPhone(s.father_phone).includes(qDigits)
        || cleanPhone(s.mother_phone).includes(qDigits)
        || cleanPhone(s.student_phone).includes(qDigits)
      );
    });
  }, [students, hay, search, gradeFilter, sectionFilter, todayFilter, payFilter, enrollFilter]);

  // Counted over the pupils the enrolment filter admits, not the raw
  // fetch: with «مغادر» selected the request returns everyone (the API
  // has no "leavers only" mode), so counting `students` would show the
  // whole school above a list of five leavers.
  const inScope = useMemo(() => {
    if (enrollFilter === 'left') return students.filter((s) => s.status !== 'active');
    return students;
  }, [students, enrollFilter]);

  const counts = useMemo(() => {
    const c = { all: inScope.length, present: 0, absent: 0, late: 0, no_register: 0 };
    inScope.forEach((s) => { c[s.today_status] = (c[s.today_status] || 0) + 1; });
    return c;
  }, [inScope]);

  const selected = students.find((s) => s.id === selectedId) || null;

  const resetFilters = () => {
    setSearch(''); setGradeFilter(''); setSectionFilter('');
    setTodayFilter('all'); setPayFilter('');
  };

  // Navbar search lands here with one of:
  //   { studentId, tab? }  open that pupil's profile (on that tab)
  //   { sectionId }        list one section
  //   { query }            prefill the search box (e.g. a bus number)
  // A pupil who has left is not in the default «نشط» list,
  // so the list is widened to «الكل» and the intent retried once the
  // wider list has arrived (widenedFrom = the rows it must replace).
  //   { view }             switch شؤون الطلاب's view (tabs, Back/Forward)
  const widenedFrom = useRef(null);
  const [scrollTo, setScrollTo] = useState(null);
  useNavIntent('student-directory', (it) => {
    if (it.view) { onView(it.view); return; }
    onView('search');
    if (it.studentId) {
      if (!data || loading) return false;
      if (widenedFrom.current && data === widenedFrom.current) return false;
      const at = students.findIndex((s) => s.id === it.studentId);
      const found = at >= 0;
      if (!found && enrollFilter === 'enrolled' && isOffice) {
        widenedFrom.current = data;
        setEnrollFilter('all');
        return false;
      }
      widenedFrom.current = null;
      if (!found) { toast.error('تعذّر العثور على الطالب في القائمة'); return; }
      resetFilters();
      // Render far enough down the list that their row exists to scroll to.
      setLimit(Math.max(60, at + 20));
      setSelectedId(it.studentId);
      setTab(it.tab || 'overview');
      setScrollTo(it.studentId);
      return;
    }
    resetFilters();
    setSelectedId(null);
    if (it.sectionId) setSectionFilter(String(it.sectionId));
    if (it.query) setSearch(it.query);
  }, [data, loading, enrollFilter]);

  // Bring the opened pupil into view: their row in the list on a wide
  // screen, the profile itself on a phone (where it sits under the list).
  useEffect(() => {
    if (!scrollTo) return;
    const id = requestAnimationFrame(() => {
      setScrollTo(null);
      if (window.innerWidth < 900) {
        document.querySelector('.sd-detail')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
        return;
      }
      // The list is its own scroller: centre the row inside it directly.
      // scrollIntoView would also move the page, and two smooth scrolls
      // at once lose one of them.
      const list = document.querySelector('.sd-list');
      const row = list?.querySelector('.sd-row.is-selected');
      if (!row) return;
      const delta = row.getBoundingClientRect().top - list.getBoundingClientRect().top;
      list.scrollTop += delta - (list.clientHeight - row.offsetHeight) / 2;
      window.scrollTo({ top: 0 });
    });
    return () => cancelAnimationFrame(id);
  }, [scrollTo]);

  return (
    <PageShell
      title="شؤون الطلاب"
      description="بحث سريع، حالة اليوم، هواتف الأهل، والإجراءات — كل شيء في شاشة واحدة"
      actions={
        <>
          <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} style={{ width: 150 }} />
          <Button variant="secondary" onClick={reload} loading={revalidating}>
            <MdRefresh size={16} /> تحديث
          </Button>
        </>
      }
    >
      {tabs}

      {error && <div className="ui-notice ui-notice-danger"><div className="ui-notice-body">{error}</div></div>}

      {/* Offline is stated, never implied: this screen drives phone
          calls home, so the office must know when it is reading a
          snapshot rather than today's truth. */}
      {offline && (
        <div className="ui-notice ui-notice-warning">
          <div className="ui-notice-body">
            لا يوجد اتصال — البيانات المعروضة محفوظة{age ? ` منذ ${formatAge(age)}` : ''}.
          </div>
        </div>
      )}

      {/* The four day-counts used to be full StatCards stacked above the
          filters and the chips — three separate bands that together ate
          the whole viewport before a single student was visible. They are
          one compact inline strip now: same numbers, a fraction of the
          height, so the list starts near the top of the screen. */}
      <div className="sd-toolbar">
        <div className="sd-counts">
          <span className="sd-count">
            <MdGroup size={15} /><strong>{counts.all}</strong><span>طالب</span>
          </span>
          <span className="sd-count is-success">
            <strong>{counts.present}</strong><span>حاضر</span>
          </span>
          <span className="sd-count is-danger">
            <strong>{counts.absent}</strong><span>غائب</span>
          </span>
          <span className="sd-count is-warning">
            <strong>{counts.late}</strong><span>متأخر</span>
          </span>
        </div>

        {/* Reaching a pupil who left: same screen, same profile, one
            click — rather than a separate archive page the office has
            to remember exists. */}
        {/* A teacher's list is their current classes only (the API never
            returns leavers to them), so the switch would only show an
            empty "مغادر". */}
        {isOffice && <div className="ui-segmented" role="tablist">
          {[
            { v: 'enrolled', label: 'نشط' },
            { v: 'left', label: 'مغادر' },
            { v: 'all', label: 'الكل' },
          ].map((o) => (
            <button
              key={o.v}
              type="button"
              className={`ui-segmented-btn ${enrollFilter === o.v ? 'is-active' : ''}`}
              onClick={() => { setEnrollFilter(o.v); setSelectedId(null); setLimit(60); }}
            >
              {o.label}
            </button>
          ))}
        </div>}
      </div>

      <div className="sd-filters">
        <div className="sd-search-wrap" data-guide="sd-search">
          <span className="sd-search-icon"><MdSearch size={17} /></span>
          <Input
            value={search}
            onChange={(e) => { setSearch(e.target.value); setLimit(60); }}
            placeholder="ابحث بالاسم، الرقم الوطني، هاتف الأب/الأم، رقم الباص..."
          />
        </div>
        <Select value={gradeFilter} onChange={(e) => setGradeFilter(e.target.value)} placeholder="كل الصفوف" options={grades} />
        <Select
          value={sectionFilter}
          onChange={(e) => setSectionFilter(e.target.value)}
          placeholder="كل الشعب"
          options={sections.map((s) => ({ value: s.id, label: s.section_label || s.title }))}
        />
        {/* Fees are the manager's: the API drops them for everyone else. */}
        {isAdmin && (
          <Select
            value={payFilter}
            onChange={(e) => setPayFilter(e.target.value)}
            placeholder="كل حالات الدفع"
            options={['سدد بالكامل', 'سدد جزئياً', 'لم يسدد', 'أعفاء من القسط', 'بلا رسوم']}
          />
        )}
      </div>

      <div className="sd-chips">
        {[
          { id: 'all', label: 'الكل' },
          { id: 'present', label: 'حاضر' },
          { id: 'absent', label: 'غائب' },
          { id: 'late', label: 'متأخر' },
          { id: 'no_register', label: 'لم يُتفقّد' },
        ].map((c) => (
          <button
            key={c.id}
            type="button"
            className={`sd-chip${todayFilter === c.id ? ' is-active' : ''}`}
            onClick={() => { setTodayFilter(c.id); setLimit(60); }}
          >
            {c.label}
            <span className="sd-chip-count">{counts[c.id] ?? 0}</span>
          </button>
        ))}
        {(search || gradeFilter || sectionFilter || payFilter || todayFilter !== 'all') && (
          <button type="button" className="sd-chip" onClick={resetFilters}>مسح الفلاتر</button>
        )}
      </div>

      <div className="sd-split">
        <div className="sd-list" data-guide="sd-list">
          {loading ? (
            <div className="sd-list-foot">جارٍ التحميل...</div>
          ) : filtered.length === 0 ? (
            <div className="sd-list-foot">لا توجد نتائج مطابقة</div>
          ) : (
            <>
              {filtered.slice(0, limit).map((s) => (
                <button
                  key={s.id}
                  type="button"
                  className={`sd-row${s.id === selectedId ? ' is-selected' : ''}`}
                  onClick={() => { setSelectedId(s.id); setTab('overview'); }}
                >
                  <span className="sd-row-avatar" title={STATUS_LABEL[s.today_status]}>
                    {initials(s.name)}
                    <span className={`sd-dot sd-dot-${s.today_status}`} />
                  </span>
                  <span className="sd-row-main">
                    <span className="sd-row-name">{s.name}</span>
                    <span className="sd-row-sub">
                      {s.section_label || 'بلا شعبة'}
                      {s.bus_number ? ` • باص ${s.bus_number}` : ''}
                    </span>
                  </span>
                </button>
              ))}
              {filtered.length > limit && (
                <div className="sd-list-foot">
                  <Button variant="secondary" onClick={() => setLimit((n) => n + 100)}>
                    عرض المزيد ({filtered.length - limit} متبقٍ)
                  </Button>
                </div>
              )}
              <div className="sd-list-foot">{filtered.length} من {students.length} طالب</div>
            </>
          )}
        </div>

        <div className="sd-detail">
          {!selected ? (
            <Card>
              <EmptyState
                icon={<MdPerson size={40} />}
                title="اختر طالباً"
                message="اختر طالباً من القائمة لعرض بياناته الكاملة، هواتف الأهل، وإجراءات سريعة."
              />
            </Card>
          ) : (
            <StudentDetail
              key={selected.id}
              student={selected}
              date={date}
              tab={tab}
              onTab={setTab}
              user={user}
              isOffice={isOffice}
              isAdmin={isAdmin}
              toast={toast}
              onChanged={reload}
              // Back at school: keep him on screen. Under «مغادر» he
              // would vanish from the list the moment the reload lands.
              onReactivated={() => {
                if (enrollFilter === 'left') setEnrollFilter('all');
                reload();
              }}
            />
          )}
        </div>
      </div>
    </PageShell>
  );
}

/* ------------------------------------------------------------------ */
/* Detail pane                                                         */
/* ------------------------------------------------------------------ */

function StudentDetail({ student, date, tab, onTab, user, isOffice, isAdmin, toast, onChanged, onReactivated }) {
  const [paying, setPaying] = useState(false);
  const [reactivating, setReactivating] = useState(false);
  const [logins, setLogins] = useState(false);
  const [receipt, setReceipt] = useState(null);
  // Which official document is open for printing, if any.
  const [printing, setPrinting] = useState(null); // 'dossier' | 'record' | 'marks'

  return (
    <>
      <div className="sd-hero" data-guide="sd-hero">
        <div className="sd-avatar">{initials(student.name)}</div>
        <div className="sd-hero-text">
          <h2 className="sd-hero-name">{student.name}</h2>
          <div className="sd-hero-meta">
            <span className="sd-hero-badge">{student.section_label || 'بلا شعبة'}</span>
            <span className="sd-hero-badge">{STATUS_LABEL[student.today_status]} — {date}</span>
            {isAdmin && <span className="sd-hero-badge">{student.payment_status}</span>}
            {student.status !== 'active' && (
              <span className="sd-hero-badge">ملغى قيده{student.leave_reason ? ` — ${student.leave_reason}` : ''}</span>
            )}
          </div>
        </div>
        <div className="sd-hero-actions">
          {isOffice && student.status !== 'active' && (
            <Button variant="success" onClick={() => setReactivating(true)}>
              <MdRestore size={16} /> تفعيل القيد
            </Button>
          )}
          {/* Login trouble is solved here: the pupil's and parents'
              accounts, and a رمز الدخول on the spot. */}
          {isOffice && (
            <Button variant="secondary" onClick={() => setLogins(true)} data-guide="sd-logins">
              <MdVpnKey size={16} /> حسابات الدخول
            </Button>
          )}
          {isOffice && (
            <Button variant="secondary" onClick={() => setPrinting('dossier')}>
              <MdFolderShared size={16} /> الإضبارة
            </Button>
          )}
          <Button variant="secondary" onClick={() => setPrinting('record')}>
            <MdPrint size={16} /> وثيقة طالب
          </Button>
          <Button variant="secondary" onClick={() => setPrinting('marks')}>
            <MdPrint size={16} /> كشف علامات
          </Button>
          {isAdmin && (
            <Button variant="success" onClick={() => setPaying(true)} data-guide="sd-pay">
              <MdPayments size={16} /> قبض قسط
            </Button>
          )}
        </div>
      </div>

      {isOffice && <StudentCodesStrip key={logins ? "open" : "closed"} student={student} onOpen={() => setLogins(true)} />}

      <Tabs
        active={tab}
        onChange={onTab}
        items={[
          { id: 'overview', label: 'نظرة عامة' },
          { id: 'enrollments', label: 'القيود' },
          { id: 'attendance', label: 'الحضور' },
          { id: 'exams', label: 'العلامات' },
          ...(isAdmin ? [{ id: 'payments', label: 'الأقساط' }] : []),
        ]}
      />

      <div style={{ marginTop: 'var(--ui-space-4)' }}>
        {tab === 'overview' && <OverviewTab student={student} />}
        {tab === 'enrollments' && <EnrollmentsTab student={student} />}
        {tab === 'attendance' && <AttendanceTab student={student} />}
        {tab === 'exams' && <ExamsTab student={student} />}
        {tab === 'payments' && isAdmin && <PaymentsTab student={student} />}
      </div>

      {paying && (
        <PayModal
          student={student}
          user={user}
          toast={toast}
          onClose={() => setPaying(false)}
          onPaid={(r) => { setPaying(false); setReceipt(r); onChanged(); }}
        />
      )}

      {receipt && <ReceiptModal receipt={receipt} student={student} onClose={() => setReceipt(null)} />}

      {reactivating && (
        <ReactivateStudentModal
          student={student}
          toast={toast}
          onClose={() => setReactivating(false)}
          onDone={() => { setReactivating(false); onReactivated(); }}
        />
      )}

      {logins && <StudentLoginsModal student={student} toast={toast} onClose={() => setLogins(false)} />}

      {printing === 'dossier' && <DossierPrint studentId={student.id} onClose={() => setPrinting(null)} />}
      {printing && printing !== 'dossier' && (
        <StudentDocModal kind={printing} student={student} onClose={() => setPrinting(null)} />
      )}
    </>
  );
}

function OverviewTab({ student }) {
  const hasContacts = student.father_phone || student.mother_phone || student.student_phone;
  return (
    <div className="sd-cards">
      <Card title="الاتصال بالأهل">
        {hasContacts ? (
          <div className="sd-contacts">
            <Contact who="الأب" name={student.father_name} phone={student.father_phone} />
            <Contact who="الأم" name={student.mother_name} phone={student.mother_phone} />
            <Contact who="الطالب" phone={student.student_phone} />
          </div>
        ) : (
          <p className="ui-field-hint">لا توجد أرقام هواتف مسجّلة لهذه العائلة.</p>
        )}
        {student.address && (
          <div className="sd-info-grid" style={{ marginTop: 'var(--ui-space-3)' }}>
            <Info label="العنوان" value={student.address} />
            <Info label="الأمانة" value={student.amana} />
          </div>
        )}
      </Card>

      <Card title="بيانات الطالب">
        <div className="sd-info-grid">
          <Info label="الصف" value={student.grade_title} />
          <Info label="الشعبة" value={student.section_label} />
          <Info label="الجنس" value={student.gender === 'male' ? 'ذكر' : student.gender === 'female' ? 'أنثى' : null} />
          <Info label="تاريخ الميلاد" value={student.birth_date} />
          <Info label="الرقم الوطني" value={student.national_id} />
          <Info label="رقم التسجيل" value={formatRegNo(student.registration_number)} />
          <Info label="اسم الأب" value={student.father_name} />
          <Info label="اسم الأم" value={student.mother_name} />
          <Info label="الكنية" value={student.family_last_name} />
        </div>
        {student.notes && (
          <p className="ui-field-hint" style={{ marginTop: 'var(--ui-space-3)' }}>
            <strong>ملاحظات:</strong> {student.notes}
          </p>
        )}
      </Card>

      {student.bus_number && (
        <Card title="المواصلات">
          <div className="sd-info-grid">
            <Info label="رقم الباص" value={student.bus_number} />
            <Info label="السائق" value={student.driver_name} />
          </div>
        </Card>
      )}
    </div>
  );
}

/**
 * القيود — this pupil's enrollment history: the section they are in now,
 * and every section they were in before.
 *
 * The API already sorts newest-first and joins the section label, so the
 * row with no end_date is the current one. A pupil must never have two
 * of those at once (schema rule R14) — if it ever happens, this screen
 * is where the office would notice, so the current ones are shown as a
 * distinct, obvious block rather than blended into the list.
 */
/**
 * Opens one of the official documents for printing.
 *
 * It refetches rather than passing the directory row straight in: a
 * directory row is missing registry_place / registry_number /
 * birth_place / registration_date, and getStudent does not join the
 * family — so a formal record built from the list would silently print
 * blank fields. The full record needs api.student + api.family.
 */
function StudentDocModal({ kind, student, onClose }) {
  const { data, loading, error } = useData(async () => {
    if (kind === 'marks') {
      const [full, results] = await Promise.all([
        api.student(student.id),
        api.studentReportCard(student.id),
      ]);
      return { full, results };
    }
    const full = await api.student(student.id);
    // A student without a family row is valid (family_id nullable), so
    // a failed/absent family lookup must not block the document.
    let family = null;
    if (full?.family_id) {
      family = await api.family(full.family_id).catch(() => null);
    }
    return { full, family };
  }, [kind, student.id]);

  return (
    <Modal
      open
      size="lg"
      title={kind === 'marks' ? 'كشف علامات' : 'وثيقة طالب'}
      onClose={onClose}
      footer={<Button variant="secondary" onClick={onClose}>إغلاق</Button>}
    >
      {loading ? <p>جارٍ التحميل...</p>
        : error ? <div className="ui-notice ui-notice-danger"><div className="ui-notice-body">{error}</div></div>
        : kind === 'marks'
          ? <MarksSheetDoc student={data.full} results={data.results || []} showToolbar />
          : <StudentRecordDoc student={data.full} family={data.family} showToolbar />}
    </Modal>
  );
}

function EnrollmentsTab({ student }) {
  const { data, loading } = useData(() => api.enrollments({ student_id: student.id }), [student.id]);
  const rows = data || [];
  const current = rows.filter((e) => !e.end_date);
  const past = rows.filter((e) => e.end_date);

  return (
    <Card title="القيود">
      {loading ? <p>جارٍ التحميل...</p> : rows.length === 0 ? (
        <p className="ui-field-hint">لا يوجد أي قيد مسجّل لهذا الطالب.</p>
      ) : (
        <>
          <div className="sd-enr-head">القيد الحالي</div>
          {current.length === 0 ? (
            <p className="ui-field-hint">
              لا يوجد قيد فعّال — الطالب غير مسجّل في أي شعبة حالياً.
            </p>
          ) : (
            current.map((e) => (
              <div className="sd-enr-row is-current" key={e.id}>
                <span className="sd-enr-section">{e.section_label || e.section_title || `شعبة #${e.section_id}`}</span>
                <span className="sd-enr-dates">من {e.start_date}</span>
                <span className="ui-badge ui-badge-success">فعّال</span>
              </div>
            ))
          )}

          {past.length > 0 && (
            <>
              <div className="sd-enr-head">قيود سابقة</div>
              {past.map((e) => (
                <div className="sd-enr-row" key={e.id}>
                  <span className="sd-enr-section">{e.section_label || e.section_title || `شعبة #${e.section_id}`}</span>
                  <span className="sd-enr-dates">{e.start_date} ← {e.end_date}</span>
                  <span className="ui-badge ui-badge-neutral">منتهٍ</span>
                </div>
              ))}
            </>
          )}
        </>
      )}
    </Card>
  );
}

function AttendanceTab({ student }) {
  const { data, loading } = useData(() => api.studentAttendance(student.id), [student.id]);
  const marks = data || [];
  // Totals cover the pupil's WHOLE record, including days marked in a
  // section they have since transferred out of — the child was absent
  // that day regardless of which class they sat in. (A section's own
  // figures deliberately exclude those; see attendance.js.)
  const absent = marks.filter((m) => m.status === 'absent').length;
  const late = marks.filter((m) => m.status === 'late').length;
  const transferred = marks.filter((m) => m.is_transferred).length;

  return (
    <Card title="سجل الغياب والتأخير">
      {loading ? <p>جارٍ التحميل...</p> : (
        <>
          <StatGrid>
            <StatCard label="أيام الغياب" value={absent} tone="danger" />
            <StatCard label="مرات التأخير" value={late} tone="warning" />
            {transferred > 0 && (
              <StatCard label="من شعبة سابقة" value={transferred} tone="neutral" hint="سُجّلت قبل النقل" />
            )}
          </StatGrid>
          {marks.length === 0 ? (
            <p className="ui-field-hint" style={{ marginTop: 'var(--ui-space-3)' }}>
              لا توجد أي مخالفات حضور مسجّلة — الطالب حاضر في كل الأيام التي جرى فيها التفقّد.
            </p>
          ) : (
            /* The pupil's record follows the PUPIL, so it includes days
               marked in a section they have since left. Those carry a
               «نقل» badge and the old section's name — without it the
               office sees a date it cannot place, and wonders why the
               current class has no matching register for it. */
            <ul className="sd-mark-list" style={{ marginTop: 'var(--ui-space-4)' }}>
              {marks.slice(0, 40).map((m) => (
                <li className="sd-mark-row" key={`${m.date}-${m.section_id ?? 'x'}`}>
                  <span className="sd-mark-date">{m.date}</span>
                  <span className={`ui-badge ui-badge-${m.status === 'absent' ? 'danger' : 'warning'}`}>
                    {STATUS_LABEL[m.status]}
                  </span>
                  {m.is_transferred ? (
                    <span className="ui-badge ui-badge-neutral" title={`سُجّل في ${m.section_label || 'شعبة سابقة'} قبل النقل`}>
                      نقل — {m.section_label || 'شعبة سابقة'}
                    </span>
                  ) : null}
                  {m.note ? <span className="sd-mark-note">{m.note}</span> : null}
                </li>
              ))}
            </ul>
          )}
        </>
      )}
    </Card>
  );
}

function ExamsTab({ student }) {
  const { data, loading } = useData(() => api.studentReportCard(student.id), [student.id]);
  const card = data || null;
  const results = card?.results || card?.exams || (Array.isArray(card) ? card : []);

  return (
    <Card title="نتائج الامتحانات">
      {loading ? <p>جارٍ التحميل...</p>
        : results.length === 0 ? <p className="ui-field-hint">لا توجد نتائج امتحانات بعد.</p>
        : (
          <div className="sd-info-grid">
            {results.map((r, i) => (
              <Info
                key={r.exam_id ?? i}
                label={`${r.subject_title || r.subject || ''} ${r.exam_title ? `— ${r.exam_title}` : ''}`}
                value={`${r.marks_obtained ?? '—'} / ${r.total_marks ?? '—'}`}
              />
            ))}
          </div>
        )}
    </Card>
  );
}

function PaymentsTab({ student }) {
  const { data, loading } = useData(() => api.payments({ student_id: student.id }), [student.id]);
  const rows = data || [];

  return (
    <Card title="سجل الأقساط">
      <StatGrid>
        <StatCard label="المستحق حتى الآن" value={money(student.fees_total_cents)} tone="info" />
        <StatCard label="المدفوع" value={money(student.total_paid_cents)} tone="success" />
        <StatCard
          label="المتبقي"
          value={money(student.remaining_cents)}
          tone={student.remaining_cents > 0 ? 'danger' : 'success'}
        />
      </StatGrid>
      <div style={{ marginTop: 'var(--ui-space-4)' }}>
        {loading ? <p>جارٍ التحميل...</p>
          : rows.length === 0 ? <p className="ui-field-hint">لا توجد دفعات مسجّلة.</p>
          : (
            <div className="sd-info-grid">
              {rows.map((p) => (
                <Info
                  key={p.id}
                  label={`${(p.paid_at || '').slice(0, 10)} — ${p.payment_type}`}
                  value={money(p.amount_cents)}
                />
              ))}
            </div>
          )}
      </div>
    </Card>
  );
}

/* ------------------------------------------------------------------ */
/* Pay + receipt — the "no need to open الأقساط" flow                  */
/* ------------------------------------------------------------------ */

function PayModal({ student, user, toast, onClose, onPaid }) {
  const [form, setForm] = useState({
    amount_cents: student.remaining_cents || '',
    payment_type: 'قسط',
    payment_method: 'cash',
    note: '',
  });
  const [saving, setSaving] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const isExempt = form.payment_type === 'أعفاء من القسط';

  const submit = async (e) => {
    e.preventDefault();
    if (!isExempt && (!form.amount_cents || Number(form.amount_cents) <= 0)) {
      return toast.error('المبلغ مطلوب');
    }
    setSaving(true);
    try {
      const payment = await api.createPayment({
        student_id: student.id,
        amount_cents: isExempt ? 0 : Number(form.amount_cents),
        payment_type: form.payment_type,
        payment_method: isExempt ? null : form.payment_method,
        note: form.note || null,
        paid_by: user.id,
      });
      toast.success('تم قبض القسط');
      onPaid(payment);
    } catch (err) { toast.error(err.message); }
    finally { setSaving(false); }
  };

  return (
    <Modal
      open
      size="sm"
      title={`قبض قسط: ${student.name}`}
      onClose={onClose}
      footer={(requestClose) => (
        <>
          <Button variant="secondary" onClick={requestClose}>إلغاء</Button>
          <Button variant="success" onClick={submit} loading={saving} data-guide="modal-save">قبض وطباعة الإيصال</Button>
        </>
      )}
    >
      <form onSubmit={submit}>
        <p className="ui-modal-message" style={{ marginBottom: 'var(--ui-space-3)' }}>
          المتبقي على الطالب: <strong>{money(student.remaining_cents)}</strong> من أصل {money(student.fees_total_cents)}
        </p>
        <FormGrid columns={2}>
          <Field label="نوع الدفعة">
            <Select value={form.payment_type} onChange={set('payment_type')} options={['قسط', 'أعفاء من القسط']} />
          </Field>
          {!isExempt && (
            <>
              <Field label="المبلغ" required guide="pay-amount">
                <Input type="number" value={form.amount_cents} onChange={set('amount_cents')} autoFocus />
              </Field>
              <Field label="طريقة الدفع">
                <Select
                  value={form.payment_method}
                  onChange={set('payment_method')}
                  options={[
                    { value: 'cash', label: 'نقداً' },
                    { value: 'transfer', label: 'حوالة' },
                    { value: 'other', label: 'أخرى' },
                  ]}
                />
              </Field>
            </>
          )}
          <Field label="ملاحظة" span><Textarea value={form.note} onChange={set('note')} rows={2} /></Field>
        </FormGrid>
      </form>
    </Modal>
  );
}

/**
 * The receipt handed over the counter after قبض قسط.
 *
 * Renders PaymentReceiptDoc (ui/documents) inside the modal rather than
 * a hand-rolled layout: that component owns the A5 paper size, the
 * letterhead, the amount-in-words anti-forgery line and the signature
 * blocks, and PrintDocument scopes window.print() to the sheet alone.
 * The previous version here printed via `visibility: hidden` on
 * `body *`, which left the hidden chrome still occupying pages — the
 * blank-page-plus-stray-marks output.
 */
function ReceiptModal({ receipt, student, onClose }) {
  return (
    <Modal open size="lg" title="وصل دفع قسط" onClose={onClose}>
      <PaymentReceiptDoc
        payment={receipt}
        student={student}
        cashierName={session.user()?.name}
        onClose={onClose}
      />
    </Modal>
  );
}
